import LayoutHR from "layouts/layout-hr";
import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import Swal from "sweetalert2";
import MasterDataFormV2 from "../MasterDataFormV2";
import { OvertimeRequestsProvider, useOvertimeRequests } from "./context/OvertimeRequestsContext";
import { getDevelopmentBanner } from "utils/devNotice";
import { useNavigate } from "react-router-dom";
import { deleteRequest, getRequest, postRequest } from "services/api";
import { useUser } from "contexts/v1/masterData/UserContext";

function OvertimeRequestsContent() {
  const { overtimeRequests, filterOptions, handleSearch, handleSearchChange, refetch, Pagination } =
    useOvertimeRequests();
  const { users } = useUser();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [overtimePolicies, setOvertimePolicies] = useState([]);

  const { handleError, handleSuccessToast } = useCanvasHandler();

  const handleCreate = () => {
    setShowModal(true);
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setShowModal(true);
    setEditingItem(item);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteRequest(`/ahr/overtime-requests/${id}`);
        if (response.success) {
          handleSuccessToast(true, "Overtime request deleted successfully");
          refetch();
        } else {
          handleError("Failed to delete overtime request");
        }
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleApprove = async (id) => {
    try {
      const response = await postRequest(`/ahr/overtime-requests/${id}/approve`);
      if (response.success) {
        handleSuccessToast(true, "Overtime request approved");
        refetch();
      } else {
        handleError(response.message || "Failed to approve");
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleComplete = async (id, actualHours) => {
    try {
      const response = await postRequest(`/ahr/overtime-requests/${id}/complete`, { actual_hours: actualHours });
      if (response.success) {
        handleSuccessToast(true, "Overtime request completed");
        refetch();
      } else {
        handleError(response.message || "Failed to complete");
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingItem(null);
    refetch();
    handleSuccessToast(true, "Overtime request saved successfully");
  };

  const handleFormError = (error) => {
    handleError(error);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  // Fetch Overtime Policies
  React.useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const response = await getRequest("/ahr/overtime-policies");
        if (response.success) {
          setOvertimePolicies(response.data.map(p => ({
            value: p.id,
            label: p.name
          })));
        }
      } catch (error) {
        console.error("Failed to fetch policies:", error);
      }
    };
    fetchPolicies();
  }, []);

  const formFields = [
    {
      name: "user_id",
      label: "Employee",
      type: "select",
      required: true,
      options: users.data?.map(user => ({
        value: user.id,
        label: user.name
      })),
      placeholder: "Search employee...",
      hidden: !!editingItem, // Hide user selection in edit mode
    },
    {
      name: "overtime_policy_id",
      label: "Overtime Policy",
      type: "select",
      options: overtimePolicies,
      placeholder: "Select policy (optional)",
      hidden: !!editingItem, // Hide policy selection in edit mode
    },
    {
      name: "overtime_date",
      label: "Overtime Date",
      type: "date",
      required: true,
    },
    {
      name: "start_time",
      label: "Start Time",
      type: "time",
      required: true,
    },
    {
      name: "end_time",
      label: "End Time",
      type: "time",
      required: true,
    },
    {
      name: "planned_hours",
      label: "Planned Hours",
      type: "number",
      required: true,
      min: 0.5,
    },
    {
      name: "reason",
      label: "Reason",
      type: "textarea",
      required: true,
    },
  ].filter(f => !f.hidden);

  return (
    <LayoutHR>
      {getDevelopmentBanner()}
      <div className="container-xl">
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col-auto">
              <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                Back
              </button>
            </div>
            <div className="col">
              <h2 className="page-title">Overtime Requests</h2>
              <div className="text-muted mt-1">Manage employee overtime requests</div>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary" onClick={handleCreate}>
                Add Request
              </button>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search requests..."
                  value={filterOptions.search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={handleSearch}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-vcenter table-hover">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Action</th>
                    <th>Employee</th>
                    <th>Date</th>
                    <th>Planned Hours</th>
                    <th>Actual Hours</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {overtimeRequests.loading ? (
                    <tr>
                      <td colSpan="6" className="text-center">Loading...</td>
                    </tr>
                  ) : overtimeRequests.data?.length > 0 ? (
                    overtimeRequests.data.map((item, index) => (
                      <tr key={item.id}>
                        <td>{(filterOptions.currentPage - 1) * 10 + index + 1}</td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle variant="default" size="sm">
                              Actions
                            </Dropdown.Toggle>
                            <Dropdown.Menu style={{ zIndex: 2050 }}>
                              <Dropdown.Item onClick={() => handleEdit(item)}>Edit</Dropdown.Item>
                              <Dropdown.Item onClick={() => handleApprove(item.id)} className="text-success">Approve</Dropdown.Item>
                              <Dropdown.Item onClick={() => {
                                Swal.fire({
                                  title: 'Input Actual Hours',
                                  input: 'number',
                                  showCancelButton: true,
                                  confirmButtonText: 'Submit',
                                }).then((result) => {
                                  if (result.isConfirmed) {
                                    handleComplete(item.id, result.value);
                                  }
                                });
                              }} className="text-info">Complete</Dropdown.Item>
                              <Dropdown.Item onClick={() => handleDelete(item.id)} className="text-danger">Delete</Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                        <td>{item.user?.name || '-'}</td>
                        <td>{item.overtime_date}</td>
                        <td>{item.planned_hours} h</td>
                        <td>{item.actual_hours || '-'} h</td>
                        <td>
                          <span className={`badge bg-${item.status === 'approved' ? 'success' : item.status === 'completed' ? 'info' : 'warning'}`}>
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">No requests found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {overtimeRequests.pagination && <Pagination />}
          </div>
        </div>

        {showModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingItem ? "Edit Request" : "Create Request"}</h5>
                  <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                </div>
                <div className="modal-body">
                  <MasterDataFormV2
                    apiUrl="/ahr/overtime-requests"
                    initialData={editingItem}
                    fields={formFields}
                    onSuccess={handleFormSuccess}
                    onError={handleFormError}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutHR>
  );
}

export default function OvertimeRequestsPage() {
  return (
    <OvertimeRequestsProvider>
      <OvertimeRequestsContent />
    </OvertimeRequestsProvider>
  );
}
